import { Space, Spin } from 'antd';
import { isValid } from 'date-fns';
import { isEmpty } from 'lodash';

// import { Link } from 'react-router-dom';
// import { ROUTES } from 'shared/constants/ROUTES';
import { formatProgramTypePercentage } from 'shared/hooks/useProgramTypeOptions';
import { IProgram } from 'shared/interfaces/IProgram';
import { IProgramSetup } from 'shared/interfaces/IProgramSetup';
import { ITableColumn } from 'shared/interfaces/utils/ITable';
import { Button, Select } from 'shared/theme/elements';
import { getFormattedDate } from 'shared/utils/getFormattedDate';
import {
  ProgramSetupFilter,
  ProgramSetupLoading,
  ProgramSetupSearch,
  ProgramSetupSelectOptions,
} from './ListContainer';

interface Props {
  handleEdit: (id: string) => void;
  handleDelete: (
    id: string,
    // channel: Pick<IProgramSetup, 'channel_details'>['channel_details'],
    station: Pick<IProgramSetup, 'station_details'>['station_details'],

    program: Pick<IProgramSetup, 'station_program_details'>['station_program_details'],
  ) => void;
  onFilter: (key: keyof ProgramSetupFilter, value?: any) => void;
  filters: ProgramSetupFilter;
  options: ProgramSetupSelectOptions;
  loading: ProgramSetupLoading;
  onSearch: (key: keyof ProgramSetupSearch, value: any) => void;
  search: ProgramSetupSearch;
  locationState: { machine_ip: string; machine_type: string };
  // Programs on the selected station, by id: Program Type is stored on the program.
  programById: Record<string, IProgram>;
}

const dayShort = {
  sunday: 'Sun',
  monday: 'M',
  tuesday: 'T',
  wednesday: 'W',
  thursday: 'Th',
  friday: 'F',
  saturday: 'Sat',
};
const dayOrder = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

function formatTime(str) {
  if (!str) return '';
  let date = new Date(str);
  if (isNaN(date.getTime())) {
    // Try parsing as a time-only string (HH:mm:ss)
    date = new Date(`1990-01-01T${str}`);
  }
  if (isNaN(date.getTime())) return '';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
}

function isDefaultTime(str: string | undefined) {
  return !str || str === '1990-01-01 00:00:00' || str === '00:00' || str === '12:00 AM';
}

function groupTimeslots(daily_schedule: any) {
  if (!daily_schedule) return [];
  type Group = { slot: string; days: string[]; start: string; end: string };
  const daySlots = dayOrder.map((day) => {
    const start = daily_schedule[day]?.timeslot_start;
    const end = daily_schedule[day]?.timeslot_end;
    return { day, start, end };
  });
  const groups: Group[] = [];
  let current: Group | null = null;
  daySlots.forEach(({ day, start, end }) => {
    // Skip days with default/no timeslot
    if (isDefaultTime(start) && isDefaultTime(end)) {
      return;
    }
    const slot = `${start}-${end}`;
    if (!current || current.slot !== slot) {
      if (current) groups.push(current);
      current = { slot, days: [day], start, end };
    } else {
      current.days.push(day);
    }
  });
  // Always push the last group if it exists
  if (current) groups.push(current);

  // Filter out groups where both start and end are default
  return groups
    .filter(({ start, end }) => {
      const bothDefaultRaw = isDefaultTime(start) && isDefaultTime(end);
      const bothDefaultFormatted = formatTime(start) === '12:00 AM' && formatTime(end) === '12:00 AM';
      return !(bothDefaultRaw || bothDefaultFormatted);
    })
    .map(({ days, start, end }) => {
      const shortDays = days.map((d) => dayShort[d]);
      let dayLabel = shortDays[0];
      if (days.length > 1) {
        dayLabel = `${shortDays[0]}-${shortDays[shortDays.length - 1]}`;
      }
      return {
        days: dayLabel,
        timeslot: start && end ? `${formatTime(start)} – ${formatTime(end)}` : '–',
      };
    });
}

export const LIST_COLUMN = ({
  handleEdit,
  handleDelete,
  filters,
  options,
  onFilter,
  onSearch,
  loading,
  locationState,
  programById,
}: Props): ITableColumn[] => [
  // {
  //   key: 'station',
  //   title: 'Station',

  //   render: (_, data: IProgramSetup) => data.station_details?.station_name,

  //   filters: options.station,
  //   filterDropdown: ({ confirm, clearFilters }) => (
  //     <div style={{ padding: 8 }}>
  //       <Select
  //         style={{ width: '220px' }}
  //         placeHolder="Select Station"
  //         size="middle"
  //         value={filters.station}
  //         options={options.station}
  //         onChange={(value) => {
  //           if (!value) {
  //             clearFilters();
  //             onFilter('station', undefined);
  //           } else {
  //             onFilter('station', value);
  //             confirm();
  //           }
  //         }}
  //         onSearch={(value) => onSearch('station', value)}
  //         allowClear
  //         onClear={() => {
  //           clearFilters();
  //         }}
  //         filterOption={false}
  //         showSearch
  //         autoClearSearchValue
  //         loading={loading.station}
  //         notFoundContent={loading.station ? <Spin size="small" /> : null}
  //         isRequired
  //         $fullWidth
  //       ></Select>
  //     </div>
  //   ),
  //   filteredValue: filters?.station ? [filters?.station] : [],
  //   filtered: !!filters?.station,
  //   className: !isEmpty(filters.station) ? 'active-col' : '',
  // },

  {
    key: 'program',
    title: 'Program Name',
    render: (_, data: IProgramSetup) => (
      <button type="button" className="ps-linkish" onClick={() => handleEdit(data._id)}>
        {data.station_program_details?.station_program_name}
      </button>
    ),
    filterDropdown: ({ confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Select
          style={{ width: '220px' }}
          placeHolder="Select Program"
          size="middle"
          value={filters.program}
          options={options.program}
          onChange={(value) => {
            if (!value) {
              clearFilters();
              onFilter('program', undefined);
              confirm();
            } else {
              onFilter('program', value);
              confirm();
            }
          }}
          onSearch={(value) => onSearch('program', value)}
          allowClear
          onClear={() => {
            clearFilters();
          }}
          filterOption={false}
          showSearch
          autoClearSearchValue
          loading={loading.program}
          notFoundContent={loading.program ? <Spin size="small" /> : null}
          isRequired
          $fullWidth
        ></Select>
      </div>
    ),
    filteredValue: filters.program ? [filters.program] : [],
    className: !isEmpty(filters?.program) ? 'active-col' : '',
  },
  {
    key: 'programType',
    title: 'Program Type',
    render: (_, data: IProgramSetup) => {
      const program = programById[data.station_program_details?._id];
      return program?.program_type_label ? (
        <>
          <div>{program.program_type_label}</div>
          <div className="ps-mono" style={{ color: 'var(--ink-4)' }}>
            {formatProgramTypePercentage(program.program_type_percentage)}
          </div>
        </>
      ) : (
        <span style={{ color: 'var(--ink-4)' }}>—</span>
      );
    },
  },
  {
    key: 'days',
    title: 'Days',
    render: (_, data: IProgramSetup) => {
      const groups = groupTimeslots(data.daily_schedule);
      return (
        <div className="ps-mono">
          {groups.map((g, i) => (
            <div key={i}>{g.days}</div>
          ))}
        </div>
      );
    },
    // filterDropdown: ({}) => <div style={{ padding: 8 }}></div>,
    // filteredValue: [],
  },
  {
    key: 'timeslot',
    title: 'Timeslot',
    render: (_, data: any) => {
      const groups = groupTimeslots(data.daily_schedule);

      if (groups.length === 0) return <span style={{ color: '#bfbfbf' }}>–</span>;
      return (
        <div className="ps-mono">
          {groups.map((g, i) => (
            <div key={i}>{g.timeslot}</div>
          ))}
        </div>
      );
    },
    // filterDropdown: ({}) => <div style={{ padding: 8 }}></div>,
    // filteredValue: [],
  },
  // {
  //   key: 'anchors',
  //   title: 'Anchors/Reporters',
  //   width: 300,
  //   render: (_, data: IProgramSetup) =>
  //     data.anchors.map((anchor, index) => (
  //       <Link to={{ pathname: ROUTES.AUTHORS.OVERVIEW.PROFILE, state: { id: anchor._id } }}>
  //         {anchor.author_name}
  //         {data.anchors.length !== index + 1 ? ', ' : null}
  //       </Link>
  //     )),
  // },
  {
    key: 'youtube_title_setup',
    title: 'Youtube Machine',
    className: locationState?.machine_type === 'youtube' ? '' : 'hidden',
    render: (_, data: IProgramSetup) => (
      <span className="ps-mono">{data.youtube_title_setup ? data.youtube_title_setup?.machine_ip_address : ''}</span>
    ),
  },
  {
    key: 'facebook_title_setup',
    title: 'Facebook Machine',
    className: locationState?.machine_type === 'facebook' ? '' : 'hidden',
    render: (_, data: IProgramSetup) => (
      <span className="ps-mono">{data.facebook_title_setup ? data.facebook_title_setup?.machine_ip_address : ''}</span>
    ),
  },
  {
    key: 'recording_title_setup',
    title: 'Local Recording Machine',
    className: locationState?.machine_type === 'recording' ? '' : 'hidden',
    render: (_, data: IProgramSetup) => (
      <span className="ps-mono">
        {data.recording_title_setup ? data.recording_title_setup?.machine_ip_address : ''}
      </span>
    ),
  },
  {
    key: 'recordingMachine',
    title: 'Recording Machine',
    render: (_, data: IProgramSetup) =>
      data.local_recording_details?.local_recording_path ? (
        <span className="ps-mono">{data.local_recording_details.local_recording_path}</span>
      ) : (
        <span style={{ color: 'var(--ink-4)' }}>—</span>
      ),
  },
  {
    key: 'source',
    title: 'Source',
    render: (_, data: IProgramSetup) => data.local_recording_details?.source_folder || '',
  },
  {
    key: 'folderName',
    title: 'Folder Name',
    render: (_, data: IProgramSetup) =>
      data.recording_title_setup?.folder_name ? (
        <span className="ps-mono">{data.recording_title_setup.folder_name}</span>
      ) : (
        <span style={{ color: 'var(--ink-4)' }}>—</span>
      ),
  },
  // {
  //   key: 'dateCreated',
  //   title: 'Date Created',
  //   render: (_, data: IProgramSetup) => (
  //     <>
  //       <div>{getFormattedDate(new Date(data?.date_created || ''), 'LLLL dd, yyyy')}</div>
  //       <div>{getFormattedDate(new Date(data?.date_created || ''), 'hh:mm a')}</div>
  //     </>
  //   ),
  // },
  {
    key: 'dateUpdated',
    title: 'Date Modified',
    render: (_, data: IProgramSetup) =>
      isValid(new Date(data?.date_updated || '')) ? (
        <>
          <div>{getFormattedDate(new Date(data?.date_updated || ''), 'LLLL dd, yyyy')}</div>
          <div style={{ color: 'var(--ink-4)' }}>{getFormattedDate(new Date(data?.date_updated || ''), 'hh:mm a')}</div>
        </>
      ) : (
        ''
      ),
  },
  {
    key: 'actions',
    title: 'Actions',
    render: (_, { _id, station_details, station_program_details }: IProgramSetup) => {
      return (
        <Space>
          <Button onClick={() => handleEdit(_id)} key="edit">
            Edit
          </Button>
          <Button
            variant="outlined"
            onClick={() => handleDelete(_id, station_details, station_program_details)}
            key="delete"
          >
            Delete
          </Button>
        </Space>
      );
    },
  },
];
